import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, buildWhatsAppLink } from "@/lib/api";
import { formatDateLocale } from "@/lib/i18n";
import { todayISO } from "@/lib/calendar";
import { fireEnhancedConversion } from "@/lib/gtag";
import { useMobilI18n, MOBIL_DICT, MOBIL_SERVICE_KEYS, buildMobilWhatsAppText } from "@/lib/mobilI18n";

const EMPTY = { name: "", email: "", phone: "", date: "", location: "", attendees: "", eventTypeIdx: "", services: [], message: "" };
const CA = MOBIL_DICT.ca;
const labelCls = "text-xs uppercase tracking-[0.18em] text-[#5C544F]";
const inputCls = "mt-1 bg-white border-[#eae0df]";

function Field({ id, label, children }) {
  return (
    <div>
      <Label htmlFor={id} className={labelCls}>{label}</Label>
      {children}
    </div>
  );
}

export default function MobilRequestForm({ fixedDate, whatsappNumber, onDone, testPrefix = "mobil-form" }) {
  const { m } = useMobilI18n();
  const [f, setF] = useState({ ...EMPTY, date: fixedDate || "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleService = (key) =>
    setF({ ...f, services: f.services.includes(key) ? f.services.filter((s) => s !== key) : [...f.services, key] });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.phone || !f.date) return toast.error(m.form.required);
    setSubmitting(true);
    // Open the tab synchronously (inside the click) so iOS Safari doesn't block it as a popup
    const win = window.open("", "_blank");
    const serviceLabels = f.services.map((k) => CA.services.items[k].title);
    const eventType = f.eventTypeIdx !== "" ? CA.form.eventTypes[Number(f.eventTypeIdx)] : "";
    try {
      await api.post("/mobile/requests", {
        client_name: f.name, client_email: f.email, client_phone: f.phone, date: f.date,
        location: f.location, attendees: f.attendees ? Number(f.attendees) : null,
        event_type: eventType, services: serviceLabels, message: f.message,
      });
      fireEnhancedConversion({ email: f.email, phone: f.phone, name: f.name });
      const text = buildMobilWhatsAppText({ ...f, eventType }, formatDateLocale(f.date, "ca"), serviceLabels);
      const url = buildWhatsAppLink(whatsappNumber, text);
      if (win) win.location.href = url;
      else window.location.href = url;
      toast.success(m.form.sent);
      setF({ ...EMPTY, date: fixedDate || "" });
      onDone?.();
    } catch {
      win?.close();
      toast.error(m.form.error);
    } finally {
      setSubmitting(false);
    }
  };

  const tid = (s) => `${testPrefix}-${s}`;
  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid={tid("root")}>
      <Field id={tid("name")} label={m.form.name}>
        <Input id={tid("name")} data-testid={tid("name")} value={f.name} onChange={set("name")} className={inputCls} required />
      </Field>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field id={tid("email")} label={m.form.email}>
          <Input id={tid("email")} type="email" data-testid={tid("email")} value={f.email} onChange={set("email")} className={inputCls} required />
        </Field>
        <Field id={tid("phone")} label={m.form.phone}>
          <Input id={tid("phone")} data-testid={tid("phone")} value={f.phone} onChange={set("phone")} className={inputCls} required />
        </Field>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {!fixedDate && (
          <Field id={tid("date")} label={m.form.date}>
            <Input id={tid("date")} type="date" min={todayISO()} data-testid={tid("date")} value={f.date} onChange={set("date")} className={inputCls} required />
          </Field>
        )}
        <Field id={tid("attendees")} label={m.form.attendees}>
          <Input id={tid("attendees")} type="number" min="1" data-testid={tid("attendees")} value={f.attendees} onChange={set("attendees")} className={inputCls} />
        </Field>
        <Field id={tid("type")} label={m.form.eventType}>
          <Select value={f.eventTypeIdx} onValueChange={(v) => setF({ ...f, eventTypeIdx: v })}>
            <SelectTrigger className={inputCls} data-testid={tid("type")}><SelectValue placeholder={m.form.eventTypePh} /></SelectTrigger>
            <SelectContent>
              {m.form.eventTypes.map((label, i) => (
                <SelectItem key={label} value={String(i)} data-testid={tid(`type-option-${i}`)}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>
      <Field id={tid("location")} label={m.form.location}>
        <Input id={tid("location")} data-testid={tid("location")} value={f.location} onChange={set("location")} placeholder={m.form.locationPh} className={inputCls} />
      </Field>
      <div>
        <p className={labelCls}>{m.form.services}</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {MOBIL_SERVICE_KEYS.map((key) => {
            const on = f.services.includes(key);
            return (
              <button
                type="button" key={key} onClick={() => toggleService(key)} data-testid={tid(`service-${key}`)} aria-pressed={on}
                className={`px-3.5 py-1.5 rounded-full text-xs border transition-colors ${on ? "bg-[#3e664c] border-[#3e664c] text-[#fdfcf9]" : "bg-white border-[#eae0df] text-[#2A2421] hover:border-[#3e664c]/50"}`}
              >
                {m.services.items[key].title}
              </button>
            );
          })}
        </div>
      </div>
      <Field id={tid("message")} label={m.form.message}>
        <Textarea id={tid("message")} data-testid={tid("message")} value={f.message} onChange={set("message")} placeholder={m.form.messagePh} rows={3} className={inputCls} />
      </Field>
      <Button type="submit" data-testid={tid("submit")} disabled={submitting}
        className="w-full rounded-full bg-[#3e664c] hover:bg-[#2e5949] text-[#fdfcf9] py-6 text-sm uppercase tracking-[0.18em]">
        {submitting ? m.form.submitting : m.form.submit}
      </Button>
    </form>
  );
}
